import { useMemo, useState } from "react";
import {
  Box,
  IconButton,
  Link,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import Tooltip from "@mui/material/Tooltip";
import { TableSpec } from "./tableTypes";

type Order = "asc" | "desc";

const NBSP = " ";

// Parse an American-formatted number (as Qlik returns: comma thousands, dot
// decimal), returning value + decimal count, or null if not numeric.
const parseNum = (value: string): { value: number; decimals: number } | null => {
  if (value == null) return null;
  const s = String(value).trim().replace(/[\s ]/g, "").replace(/[€$*_]/g, ""); // also strip Markdown emphasis (**bold**, _italic_)
  if (!s || !/\d/.test(s) || !/^-?[\d,]*\.?\d+$/.test(s)) return null;
  const cleaned = s.replace(/,/g, "");
  const n = Number(cleaned);
  if (!Number.isFinite(n)) return null;
  const decimals = cleaned.includes(".") ? cleaned.split(".")[1].length : 0;
  return { value: n, decimals };
};

// EU notation: non-breaking-space thousands, comma decimal.
const formatEU = (value: number, decimals: number): string => {
  const sign = value < 0 ? "-" : "";
  const v = Math.abs(value);
  if (decimals > 0) {
    const fixed = v.toFixed(decimals);
    const [intPart, frac] = fixed.split(".");
    const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, NBSP);
    return `${sign}${grouped},${frac}`;
  }
  const grouped = String(Math.round(v)).replace(/\B(?=(\d{3})+(?!\d))/g, NBSP);
  return `${sign}${grouped}`;
};

const toNumber = (value: string): number | null => {
  const p = parseNum(value);
  return p ? p.value : null;
};

const compareCells = (a: string, b: string): number => {
  const na = toNumber(a);
  const nb = toNumber(b);
  if (na !== null && nb !== null) return na - nb;
  return (a ?? "").localeCompare(b ?? "", undefined, { numeric: true });
};

// Escape a cell value for safe embedding in the copied HTML table.
const escapeHtml = (s: string): string =>
  String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

interface Props {
  table: TableSpec;
}

export const TableRenderer = ({ table }: Props) => {
  const [orderBy, setOrderBy] = useState<number | null>(null);
  const [order, setOrder] = useState<Order>("asc");
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [copied, setCopied] = useState(false);

  // Per-column flags: numeric columns are right-aligned; numeric non-year
  // columns are displayed in EU notation. Year-like integer columns (1900-2100)
  // are right-aligned but not grouped.
  const { numericCols, yearCols } = useMemo(() => {
    const n = table.headers.length;
    const numeric: boolean[] = new Array(n).fill(false);
    const year: boolean[] = new Array(n).fill(false);
    for (let c = 0; c < n; c++) {
      const vals = table.rows
        .filter((r) => c < r.length && String(r[c]).trim() !== "")
        .map((r) => parseNum(r[c]));
      if (vals.length && vals.every((v) => v !== null)) {
        numeric[c] = true;
        year[c] = vals.every(
          (v) => v && Number.isInteger(v.value) && v.value >= 1900 && v.value <= 2100,
        );
      }
    }
    return { numericCols: numeric, yearCols: year };
  }, [table.headers, table.rows]);

  const displayCell = (raw: string, col: number): string => {
    if (!numericCols[col] || yearCols[col]) return raw ?? "";
    const p = parseNum(raw);
    return p ? formatEU(p.value, p.decimals) : raw ?? "";
  };

  const sortedRows = useMemo(() => {
    if (orderBy === null) return table.rows;
    const sorted = [...table.rows].sort((r1, r2) =>
      compareCells(r1[orderBy] ?? "", r2[orderBy] ?? ""),
    );
    if (order === "desc") sorted.reverse();
    return sorted;
  }, [table.rows, orderBy, order]);

  const handleSort = (col: number) => {
    if (orderBy === col) {
      setOrder(order === "asc" ? "desc" : "asc");
    } else {
      setOrderBy(col);
      setOrder("asc");
    }
  };

  const handleExport = async (format: "csv" | "xlsx") => {
    setAnchorEl(null);
    try {
      // Send the RAW rows; the backend applies EU formatting.
      const response = await fetch(`/api/export/table/${format}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ headers: table.headers, rows: sortedRows }),
      });
      if (!response.ok) throw new Error("Export failed");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `table.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      alert(`Could not export table to ${format.toUpperCase()}.`);
    }
  };

  // Copy the table to the clipboard as HTML, so it pastes as a real table in
  // Word / PowerPoint with the same EU number formatting and right-aligned
  // numeric columns as shown; a tab-separated plain-text version is included as
  // a fallback for editors that do not accept HTML.
  const handleCopy = async () => {
    const cellStyle = (c: number) =>
      `text-align:${numericCols[c] ? "right" : "left"};border:1px solid #ccc;padding:4px 8px`;
    const headHtml = table.headers
      .map((h, c) => `<th style="${cellStyle(c)};background:#f3f2f1">${escapeHtml(h)}</th>`)
      .join("");
    const bodyHtml = sortedRows
      .map(
        (row) =>
          "<tr>" +
          table.headers
            .map((_, c) => `<td style="${cellStyle(c)}">${escapeHtml(displayCell(row[c] ?? "", c))}</td>`)
            .join("") +
          "</tr>",
      )
      .join("");
    const html = `<table style="border-collapse:collapse"><thead><tr>${headHtml}</tr></thead><tbody>${bodyHtml}</tbody></table>`;
    const text =
      table.headers.join("\t") +
      "\n" +
      sortedRows
        .map((row) => table.headers.map((_, c) => displayCell(row[c] ?? "", c)).join("\t"))
        .join("\n");
    try {
      await navigator.clipboard.write([
        new ClipboardItem({
          "text/html": new Blob([html], { type: "text/html" }),
          "text/plain": new Blob([text], { type: "text/plain" }),
        }),
      ]);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      console.error(e);
      alert("Could not copy the table. Use the Excel / CSV export instead.");
    }
  };

  return (
    <Box sx={{ my: 1.5, width: "100%" }}>
      <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 0.5 }}>
        <Tooltip title={copied ? "Copied" : "Copy table"}>
          <IconButton size="small" aria-label="Copy table" onClick={handleCopy}>
            {copied ? (
              <CheckIcon fontSize="small" />
            ) : (
              <ContentCopyIcon fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
        <IconButton
          size="small"
          aria-label="Export table"
          onClick={(e) => setAnchorEl(e.currentTarget)}
        >
          <DownloadIcon fontSize="small" />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
        >
          <MenuItem onClick={() => handleExport("xlsx")}>Excel (.xlsx)</MenuItem>
          <MenuItem onClick={() => handleExport("csv")}>CSV (.csv)</MenuItem>
        </Menu>
      </Box>
      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{ maxWidth: "100%", overflowX: "auto" }}
      >
        <Table size="small" stickyHeader aria-label="results table">
          <TableHead>
            <TableRow>
              {table.headers.map((header, col) => (
                <TableCell
                  key={col}
                  align={numericCols[col] ? "right" : "left"}
                  sortDirection={orderBy === col ? order : false}
                  sx={{ fontWeight: 600, whiteSpace: "nowrap", backgroundColor: "#f3f2f1" }}
                >
                  <TableSortLabel
                    active={orderBy === col}
                    direction={orderBy === col ? order : "asc"}
                    onClick={() => handleSort(col)}
                  >
                    {header}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedRows.map((row, ri) => (
              <TableRow key={ri} hover>
                {table.headers.map((_, ci) => (
                  <TableCell
                    key={ci}
                    align={numericCols[ci] ? "right" : "left"}
                    sx={{ whiteSpace: "nowrap" }}
                  >
                    {displayCell(row[ci] ?? "", ci)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {table.dashboardUrl && (
        <Box sx={{ mt: 0.5 }}>
          <Link
            href={table.dashboardUrl}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, fontSize: 13 }}
          >
            <OpenInNewIcon fontSize="inherit" />
            Open in Qlik dashboard
          </Link>
        </Box>
      )}
    </Box>
  );
};
